import { BlockEditorData } from "@babackapp/blocks-editor/types";
import { isBlockTranslated } from "@babackapp/editor-blocks-library/blocks";
import { createContext, type ReactNode, useContext, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import useCurrentShopSuspense from "../hooks/use-current-shop-suspense";
import { EmailTemplateFormData } from "../types";
import { AVAILABLE_LOCALES } from "@babackapp/ui/components/translatable-field";


export type LocaleTranslationState = "empty" | "full" | "partial";

export type TemplateEditorLocaleObject = {
  value: string;
  label: string;
  flag: string;
  state: LocaleTranslationState;
}

interface TemplatesEditorLocaleContextType {
  currentLocale: string;
  defaultLocale: string;
  setCurrentLocale: (locale: string) => void;
  availableLocales: TemplateEditorLocaleObject[];
}


const TemplatesEditorLocaleContext = createContext<
  TemplatesEditorLocaleContextType | undefined
>(undefined);


export interface TemplatesEditorLocaleProviderProps {
  children: ReactNode;
}


export function TemplatesEditorLocaleProvider({
  children,
}: TemplatesEditorLocaleProviderProps) {
  const {
    data: {
      trackingPreferences: { availableLocales },
    },
  } = useCurrentShopSuspense();
  const [currentLocale, setCurrentLocale] = useState<string>("fr");
  const { data: shop } = useCurrentShopSuspense();

  const { control } = useFormContext<EmailTemplateFormData>();
  const content = useWatch({
    control,
    name: "content",
  });
  const subject = useWatch({
    control,
    name: "subject",
  });
  const defaultLocale =
    shop.trackingPreferences?.notificationsDefaultLocale || "en";

  const value: TemplatesEditorLocaleContextType = useMemo(
    () => ({
      currentLocale,
      setCurrentLocale,
      defaultLocale,
      availableLocales: (availableLocales || []).map((locale) => ({
        value: locale,
        label:
          AVAILABLE_LOCALES[locale as keyof typeof AVAILABLE_LOCALES]?.label ||
          locale,
        flag: AVAILABLE_LOCALES[locale as keyof typeof AVAILABLE_LOCALES]?.flag,
        state: getLocaleContentState(locale, content, subject as Record<string, string>),
      })),
    }),
    [currentLocale, setCurrentLocale, content, defaultLocale, subject, availableLocales],
  );

  return (
    <TemplatesEditorLocaleContext.Provider value={value}>
      {children}
    </TemplatesEditorLocaleContext.Provider>
  );
}


export function useEmailEditorLocale() {
  const context = useContext(TemplatesEditorLocaleContext);

  if (context === undefined) {
    throw new Error(
      "useEmailEditorLocale must be used within a EmailEditorLocaleProvider",
    );
  }
  return context;
}


function getLocaleContentState(
  locale: string,
  emailContent: BlockEditorData,
  subject: Record<string, string>,
): LocaleTranslationState {
  if (!emailContent?.beforeBlocks || !emailContent?.insideBlocks || !emailContent?.afterBlocks) return "empty";

  const allBlocks = [
    ...emailContent.beforeBlocks,
    ...emailContent.insideBlocks,
    ...emailContent.afterBlocks,
  ];
  const contentBlocks = allBlocks.filter((block) => !!block.content);

  if (contentBlocks.length === 0) {
    return "full";
  }

  const translatedBlocks = contentBlocks.filter((block) => {
    return isBlockTranslated(locale, block) || false;
  });
  const progress = (translatedBlocks.length / contentBlocks.length) * 100;

  if (!subject?.[locale]) {
    return progress === 0 ? "empty" : "partial";
  }

  return progress === 0 ? "empty" : progress >= 100 ? "full" : "partial";
}
